// Phase 9.1 — F&B Items browser (upgraded from Phase 9.0).
//
// Now backed by /fnb/analytics/reorder-status so each row shows
// current stock + status pill. SKU links to the item detail page.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, detailFromError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { StatusPill, fmtNum } from "./statusMeta";
import { ApplicabilityPicker } from "./ApplicabilityPicker";

const PAGE_SIZE = 25;

export default function FnbPoItems() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  // Phase 9.4b — admin toggle to see junk (Local items / blank) and
  // bar items in the same table. Default OFF ⇒ scope=daily.
  const [itemScope, setItemScope] = useState("daily");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        scope: "company", limit: PAGE_SIZE, offset,
        include_not_applicable: "true",
        item_scope: itemScope,
      });
      if (debouncedSearch) params.append("search", debouncedSearch);
      const [itemsRes, locsRes] = await Promise.all([
        api.get(`/fnb/analytics/reorder-status?${params}`),
        api.get(`/fnb/locations`),
      ]);
      setItems(itemsRes.data.items);
      setTotal(itemsRes.data.total);
      setLocations((locsRes.data || []).filter((l) => l.active !== false));
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setLoading(false);
    }
  }, [offset, debouncedSearch, itemScope]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOffset(0); setSelected(new Set()); }, [debouncedSearch, itemScope]);

  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + items.length, total);

  const toggleRow = (sku) => {
    const next = new Set(selected);
    if (next.has(sku)) next.delete(sku);
    else next.add(sku);
    setSelected(next);
  };

  const togglePage = () => {
    const pageSet = new Set(items.map((i) => i.sku));
    const allOnPage = items.every((i) => selected.has(i.sku));
    const next = new Set(selected);
    if (allOnPage) {
      pageSet.forEach((s) => next.delete(s));
    } else {
      pageSet.forEach((s) => next.add(s));
    }
    setSelected(next);
  };

  const saveBulkApplicability = useCallback(async (ids) => {
    setBulkSaving(true);
    try {
      const body = {
        sku_in: Array.from(selected),
      };
      if (ids === null) body.clear_applicability = true;
      else body.applicable_location_ids = ids;
      const res = await api.post("/fnb/items/bulk-applicability", body);
      toast.success(`Updated ${res.data.modified} of ${res.data.matched} item(s)`);
      setBulkModalOpen(false);
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setBulkSaving(false);
    }
  }, [selected, load]);

  const allOnPageSelected = items.length > 0 && items.every((i) => selected.has(i.sku));

  return (
    <div className="space-y-6" data-testid="fnb-po-items">
      <header className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="fnb-items-heading">
            F&amp;B Items
          </h1>
          <p className="text-sm text-muted-foreground">
            Every SKU seen in the stock CSV or the vendor-rate workbook. Select rows to bulk-set
            applicability. Click a SKU to open detail.
          </p>
        </div>
        <Button asChild variant="ghost" size="sm" data-testid="fnb-items-back">
          <Link to="/tools/fnb-po">← Dashboard</Link>
        </Button>
      </header>

      {selected.size > 0 && (
        <div
          className="flex items-center justify-between gap-3 border border-primary/40 bg-primary/5 rounded-md px-3 py-2"
          data-testid="items-bulk-action-bar"
        >
          <div className="text-sm">
            <b>{selected.size}</b> item{selected.size === 1 ? "" : "s"} selected
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setBulkModalOpen(true)}
              data-testid="items-bulk-set-applicability"
            >
              Set Applicability →
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelected(new Set())}
              data-testid="items-bulk-clear"
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base">
              {total.toLocaleString()} item{total === 1 ? "" : "s"}
            </CardTitle>
            <CardDescription className="text-xs">
              Showing {pageStart}–{pageEnd}
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <label
              className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer whitespace-nowrap"
              data-testid="fnb-items-scope-toggle-label"
            >
              <Checkbox
                checked={itemScope === "all"}
                onCheckedChange={(checked) => setItemScope(checked ? "all" : "daily")}
                data-testid="fnb-items-scope-toggle"
              />
              <span>Admin: show all (incl. bar &amp; junk)</span>
            </label>
            <Input
              placeholder="Search SKU or name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-sm"
              data-testid="fnb-items-search"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground" data-testid="fnb-items-loading">
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="text-sm text-muted-foreground" data-testid="fnb-items-empty">
              No items yet — upload a stock CSV or vendor-rate xlsx first.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table data-testid="fnb-items-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8">
                        <Checkbox
                          checked={allOnPageSelected}
                          onCheckedChange={togglePage}
                          data-testid="items-select-all-page"
                        />
                      </TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Sub-category</TableHead>
                      <TableHead>UOM</TableHead>
                      <TableHead className="text-right">Current stock</TableHead>
                      <TableHead className="text-right">Reorder</TableHead>
                      <TableHead>Applicable at</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((it) => {
                      const isSelected = selected.has(it.sku);
                      const app = it.applicable_location_ids;
                      const appLabel = !app
                        ? "All active"
                        : app.length === 0
                          ? "None"
                          : `${app.length} location${app.length === 1 ? "" : "s"}`;
                      return (
                        <TableRow
                          key={it.sku}
                          data-testid={`fnb-item-row-${it.sku}`}
                          className={
                            (isSelected ? "bg-primary/5 " : "") +
                            (it.is_menu_active === false ? "opacity-55 bg-muted/20" : "")
                          }
                        >
                          <TableCell>
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleRow(it.sku)}
                              data-testid={`item-row-select-${it.sku}`}
                            />
                          </TableCell>
                          <TableCell>
                            <StatusPill status={it.status} testid={`item-status-${it.sku}`} />
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            <Link
                              to={`/tools/fnb-po/items/${encodeURIComponent(it.sku)}`}
                              className="text-primary underline underline-offset-4"
                            >
                              {it.sku}
                            </Link>
                            {it.sku.startsWith("VMSTR_") && (
                              <Badge variant="outline" className="ml-2 text-[9px]">placeholder</Badge>
                            )}
                            {it.is_menu_active === false && (
                              <Badge
                                variant="outline"
                                className="ml-2 text-[9px] border-slate-400 text-slate-600"
                                data-testid={`item-inactive-pill-${it.sku}`}
                              >
                                Inactive
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">{it.name || "—"}</TableCell>
                          <TableCell className="text-sm">{it.category || "—"}</TableCell>
                          <TableCell className="text-sm">{it.sub_category || "—"}</TableCell>
                          <TableCell className="text-sm">{it.uom || "—"}</TableCell>
                          <TableCell className="text-right text-sm font-medium">
                            {fmtNum(it.current_stock_total)}
                          </TableCell>
                          <TableCell className="text-right text-sm text-muted-foreground">
                            {fmtNum(it.sheet_levels?.reorder)}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={app ? "secondary" : "outline"}
                              className="text-[10px] font-normal"
                              data-testid={`item-applicability-badge-${it.sku}`}
                            >
                              {appLabel}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center justify-between pt-4">
                <div className="text-xs text-muted-foreground">
                  Page {Math.floor(offset / PAGE_SIZE) + 1} of{" "}
                  {Math.max(1, Math.ceil(total / PAGE_SIZE))}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                    data-testid="fnb-items-prev"
                  >
                    ← Prev
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={offset + items.length >= total}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                    data-testid="fnb-items-next"
                  >
                    Next →
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={bulkModalOpen} onOpenChange={setBulkModalOpen}>
        <DialogContent className="max-w-2xl" data-testid="bulk-applicability-modal">
          <DialogHeader>
            <DialogTitle>Bulk set applicability</DialogTitle>
            <DialogDescription>
              Applying to <b>{selected.size}</b> selected item{selected.size === 1 ? "" : "s"}.
              Applicable-everywhere clears any existing restriction.
            </DialogDescription>
          </DialogHeader>
          <ApplicabilityPicker
            initialApplicable={null}
            activeLocations={locations}
            onSave={saveBulkApplicability}
            onCancel={() => setBulkModalOpen(false)}
            saving={bulkSaving}
            testidPrefix="bulk-applicability"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
